/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useFeatureFlag } from '@/features/globalConfig';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import ClassComponentVersion from '@/components/Cart/CommonCounterPart/PreOrderQuantitySelector/ClassComponentVersion/PreOrderV1';
import FuctionalComponentVersion from '@/components/Cart/CommonCounterPart/PreOrderQuantitySelector/FunctionalComponentVersion/PreOrderV2';
import {
  type ChildProps,
  type CounterData,
  type ProductInfo,
} from '@/components/Cart/CommonCounterPart/PreOrderQuantitySelector/types';
import { calculateItemUnitPrice } from '@/components/Cart/CommonCounterPart/PreOrderQuantitySelector/utils';

export type { CounterData, ProductInfo, ChildProps };

export { calculateItemUnitPrice };

type ParentProps = Omit<
  ChildProps,
  'labels' | 'isBopusMexicoStoreEnabled' | 'strikeThroughPricingEnabled' | 'isAcesEnabled'
>;

const labelMap = {
  label_cart_lineItem_MaximumQuantityReached: 'label_cart_lineItem_MaximumQuantityReached',
};

export const PreOrderQuantitySelector = (props: ParentProps) => {
  const { data: storeDetailsData } = useStoreDetailsData();
  const locale = useLocale();
  const isBopusEnabled = storeDetailsData?.bopusEnabled;
  const isBopusMexicoStoreEnabled = locale === countryCodes.mx && isBopusEnabled;
  const strikeThroughPricingEnabled = useFeatureFlag('SHOW_STRIKE_THROUGH_PRICING') === 'true';
  const { acesEnabled: isAcesEnabled } = useAcesFlags();
  const isPrePurchaseV2Enabled = useForteDecisionFlagWithTrueDefault('prePurchaseV2Enabled');

  const labels = useLabels(labelMap);

  if (isPrePurchaseV2Enabled) {
    return (
      <FuctionalComponentVersion
        labels={labels}
        isBopusMexicoStoreEnabled={isBopusMexicoStoreEnabled}
        strikeThroughPricingEnabled={strikeThroughPricingEnabled}
        {...props}
      />
    );
  }

  const classProps: ChildProps = {
    isAcesEnabled,
    labels,
    isBopusMexicoStoreEnabled,
    strikeThroughPricingEnabled,
    ...props,
  };
  return <ClassComponentVersion {...classProps} />;
};
